import * as Sentry from '@sentry/browser';
import ky, { isHTTPError, isNetworkError } from 'ky';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/** Normal app flow (auth walls, missing content) — noise in Sentry, not signal. */
const EXPECTED_STATUSES = new Set([401, 403, 404]);

/**
 * Response bodies carry PII on this platform — a 400 from a profile write echoes the
 * submitted values back, a 500 returns Django's error page. Report the field names so
 * the failure is still diagnosable, never the values.
 */
function describeErrorBody(data: unknown): string[] | undefined {
    if (data === null || typeof data !== 'object') return undefined;
    return Object.keys(data);
}

function getCsrfToken(): string | undefined {
    const token = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)?.[1];
    return token ? decodeURIComponent(token) : undefined;
}

export const api = ky.create({
    prefix: '/api/',
    // TanStack Query owns query retries — see query-client.ts. Enabling ky's default
    // (2) as well multiplies: 3 ky attempts × 2 TanStack attempts = 6 requests.
    retry: 0,
    timeout: 30_000, // per attempt
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    // Reports SyntaxErrors from `.json()` on 2xx bodies (WAF challenge,
    // session-expiry HTML) — `beforeError` runs inside the request pipeline
    // and can't see them.
    parseJson: (text, { request, response }) => {
        try {
            return JSON.parse(text);
        } catch (error) {
            if (response.ok) {
                Sentry.captureException(error, {
                    extra: {
                        url: request.url,
                        method: request.method,
                        status: response.status,
                        contentType: response.headers.get('content-type'),
                    },
                });
            }
            throw error;
        }
    },
    hooks: {
        beforeRequest: [
            ({ request }) => {
                if (SAFE_METHODS.has(request.method)) return;
                const token = getCsrfToken();
                if (token) request.headers.set('X-CSRFToken', token);
            },
        ],
        beforeError: [
            ({ error, request }) => {
                // A cancelled query, or a component that disconnected mid-request. Nothing
                // failed. TanStack aborts the signal threaded through `queryFn`, and ky
                // classifies an abort as neither a network error nor a timeout, so without
                // this every `cancelQueries` would report.
                if (error.name === 'AbortError') {
                    return error;
                }
                // Offline, DNS failure, connection refused, blocked by an extension —
                // the client's network, not ours, and it dwarfs everything else by volume.
                if (isNetworkError(error)) {
                    return error;
                }
                // Statuses we cannot act upon through sentry
                if (isHTTPError(error) && EXPECTED_STATUSES.has(error.response.status)) {
                    return error;
                }
                Sentry.captureException(error, {
                    extra: {
                        url: request.url,
                        method: request.method,
                        status: isHTTPError(error) ? error.response.status : undefined,
                        bodyFields: isHTTPError(error) ? describeErrorBody(error.data) : undefined,
                    },
                });
                return error;
            },
        ],
    },
});

/** Same defaults, no `/api/` prefix — HTML fragments, media, absolute URLs. */
export const http = api.extend({ prefix: '' });
